import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import { keepaApi } from "../../../services/api/omni/keepa";

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    return typeof detail === "string" ? detail : JSON.stringify(detail);
  }
  return error.message || JSON.stringify(error);
};

const KeepaCredentialsTab = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ display_name: "", api_key: "" });

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const response = await keepaApi.listCredentials();
      setCredentials(response.data || []);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCredentials();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      await keepaApi.createCredential({
        display_name: form.display_name || undefined,
        api_key: form.api_key.trim(),
        active: true,
      });
      setSuccess("Keepa API key saved.");
      setDialogOpen(false);
      setForm({ display_name: "", api_key: "" });
      await loadCredentials();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete Keepa credentials?")) return;
    try {
      setLoading(true);
      await keepaApi.deleteCredential(id);
      setSuccess("Credentials deleted.");
      await loadCredentials();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card sx={cp.card}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              Keepa Private API Key
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add API Key
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Connection test is not available yet. Store your private API access
            key from keepa.com.
          </Alert>

          {loading && credentials.length === 0 ? (
            <CircularProgress size={24} />
          ) : credentials.length === 0 ? (
            <Alert severity="info">No Keepa API key configured yet.</Alert>
          ) : (
            <List>
              {credentials.map((cred) => (
                <ListItem
                  key={cred.id}
                  sx={cp.listItem}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDelete(cred.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={cred.display_name || "Keepa API Key"}
                    secondary={
                      <>
                        {cred.active && (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            sx={{ mr: 1 }}
                          />
                        )}
                        {cred.api_key_masked}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Keepa API Key</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Display Name (optional)"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Private API Access Key"
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder="agc0bpf2i3..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.api_key.trim() || loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KeepaCredentialsTab;
